import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|Program History|Other Achievements Form';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.CandidateAccessLoginEmcOtherAchievements,
	portal: 'CP',
};
const fieldLabelDescriptionStart = 'Label for form field: ';

const AGREEMENT_CONTENT = `<p>
			As an employee of Dell Technologies, I agree to abide by all published Dell Technologies rules and
			regulations, including those regarding non-disclosure of Dell Technologies private information. I also agree
			that if requested, I will supply proof of credentials I am entering. I give Dell Technologies permission to
			share my industry credentials with Dell Technologies Management.
		</p>
		<p>
			Note: This website tracks internal certifications so that your skill profile can be complete and accurate.
			Information provided on industry certifications achieved will be used by Dell Technologies management to
			assess overall field skill sets and qualifications. List only certifications received and current (i.e. do
			not include any certifications that have expired). Self reported certifications may be audited to verify
			accuracy and validity
		</p>`;

type LocNameStart = 'CPOtherAchievementForm';
export type CPOtherAchievementFormLocNames =
	| `${LocNameStart}LabelVendor`
	| `${LocNameStart}LabelVerificationUrl`
	| `${LocNameStart}ErrorVerificationUrl`
	| `${LocNameStart}ErrorVendorRequired`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}ErrorNameRequired`
	| `${LocNameStart}LabelExpirationDate`
	| `${LocNameStart}LabelDateTaken`
	| `${LocNameStart}LabelActiveDate`
	| `${LocNameStart}LabelExpireDate`
	| `${LocNameStart}ErrorActiveDate`
	| `${LocNameStart}ErrorDateTaken`
	| `${LocNameStart}LabelExternalId`
	| `${LocNameStart}ErrorExternalId`
	| `${LocNameStart}ContentExpireHint`
	| `${LocNameStart}ContentUrlHint`
	| `${LocNameStart}ContentAgreement`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonAddCert`
	| `${LocNameStart}ButtonUpdateCert`
	| `${LocNameStart}NotificationSuccess`
	| `${LocNameStart}ErrorActiveDateMax`
	| `${LocNameStart}ErrorDateComparison`;

export const cpOtherAchievementForm: LocDefs<CPOtherAchievementFormLocNames> = {
	CPOtherAchievementFormLabelVendor: {
		defaultValue: 'Vendor/Sponsor',
		description: `${fieldLabelDescriptionStart}Vendor/Sponsor.`,
		displayPath: `${pathStart}|Vendor`,
		displayType: 'Label',
		...sharedProperties,
	},
	CPOtherAchievementFormErrorVendorRequired: {
		defaultValue: 'Vendor/Sponsor is required',
		description: `Error shown when Vendor/Sponsor is not selected.`,
		displayPath: `${pathStart}|Vendor/Sponsor required`,
		displayType: 'Error',
		...sharedProperties,
	},
	CPOtherAchievementFormLabelName: {
		defaultValue: 'Industry Certification Name',
		description: `${fieldLabelDescriptionStart}Name.`,
		displayPath: `${pathStart}|Name`,
		displayType: 'Label',
		...sharedProperties,
	},
	CPOtherAchievementFormErrorNameRequired: {
		defaultValue: 'Industry certification name is required',
		description: `Error shown when industry certification name is not selected.`,
		displayPath: `${pathStart}|Industry certification name required`,
		displayType: 'Error',
		...sharedProperties,
	},
	CPOtherAchievementFormLabelExpirationDate: {
		defaultValue: 'Expiration Date',
		description: `${fieldLabelDescriptionStart}Expiration date.`,
		displayPath: `${pathStart}|Name`,
		displayType: 'Label',
		...sharedProperties,
	},
	CPOtherAchievementFormLabelDateTaken: {
		defaultValue: 'Date Taken',
		description: `${fieldLabelDescriptionStart}Date taken.`,
		displayPath: `${pathStart}|Date taken`,
		displayType: 'Label',
		...sharedProperties,
	},
	CPOtherAchievementFormLabelExternalId: {
		defaultValue: 'Certification ID',
		description: `${fieldLabelDescriptionStart}External Id.`,
		displayPath: `${pathStart}|External Id`,
		displayType: 'Label',
		...sharedProperties,
	},
	CPOtherAchievementFormErrorExternalId: {
		defaultValue: 'Certification ID is required',
		description: `Error shown when external id is not provided.`,
		displayPath: `${pathStart}|External Id Required`,
		displayType: 'Error',
		...sharedProperties,
	},
	CPOtherAchievementFormLabelActiveDate: {
		defaultValue: 'Date Achieved',
		description: `${fieldLabelDescriptionStart}Date achieved.`,
		displayPath: `${pathStart}|Achieved Date`,
		displayType: 'Label',
		...sharedProperties,
	},
	CPOtherAchievementFormErrorActiveDate: {
		defaultValue: 'Achievement date is required',
		description: `Error shown when industry certification name is not selected.`,
		displayPath: `${pathStart}|Industry certification name required`,
		displayType: 'Error',
		...sharedProperties,
	},
	CPOtherAchievementFormErrorActiveDateMax: {
		defaultValue: 'Achievement date cannot be in the future',
		description: `Error shown when achievement date is in the future.`,
		displayPath: `${pathStart}|Achievement Date Future`,
		displayType: 'Error',
		...sharedProperties,
	},
	CPOtherAchievementFormErrorDateTaken: {
		defaultValue: 'Date taken is required',
		description: `Error shown when taken date is not selected.`,
		displayPath: `${pathStart}|Date Taken Required`,
		displayType: 'Error',
		...sharedProperties,
	},
	CPOtherAchievementFormLabelExpireDate: {
		defaultValue: 'Expiration Date',
		description: `${fieldLabelDescriptionStart}Expiration Date.`,
		displayPath: `${pathStart}|Expiration Date`,
		displayType: 'Label',
		...sharedProperties,
	},
	CPOtherAchievementFormContentExpireHint: {
		defaultValue: 'Leave this blank if the certification does not expire',
		description: 'Hint for expiration date field.',
		displayPath: `${pathStart}|Expiration Date Hint`,
		displayType: 'Content',
		...sharedProperties,
	},
	CPOtherAchievementFormContentUrlHint: {
		defaultValue:
			'Link to where this can be validated. Example: https://www.youracclaim.com/badges/1234567890',
		description: 'Hint for expiration date field.',
		displayPath: `${pathStart}|Url Hint`,
		displayType: 'Content',
		...sharedProperties,
	},
	CPOtherAchievementFormLabelVerificationUrl: {
		defaultValue: 'Verification Url',
		description: `${fieldLabelDescriptionStart}Verification Url.`,
		displayPath: `${pathStart}|Verification Url`,
		displayType: 'Label',
		...sharedProperties,
	},
	CPOtherAchievementFormErrorVerificationUrl: {
		defaultValue: 'Verification Url is required',
		description: `Error shown when verification url is not provided.`,
		displayPath: `${pathStart}|Verification Url Required`,
		displayType: 'Error',
		...sharedProperties,
	},
	CPOtherAchievementFormContentAgreement: {
		defaultValue: AGREEMENT_CONTENT,
		description: 'Agreement content by Dell.',
		displayPath: `${pathStart}|AgreementContent`,
		displayType: 'Content',
		shared: false,
		htmlAllowed: true,
		feature: HubFeature.CandidateAccessLoginEmcOtherAchievements,
		portal: 'SP',
	},
	CPOtherAchievementFormButtonCancel: {
		defaultValue: 'Cancel',
		description: `Button to cancel the form.`,
		displayPath: `${pathStart}|Cancel Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	CPOtherAchievementFormButtonAddCert: {
		defaultValue: 'Add Certification',
		description: `Button to add certification.`,
		displayPath: `${pathStart}|Add Certification Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	CPOtherAchievementFormButtonUpdateCert: {
		defaultValue: 'Update Certification',
		description: `Button to update certification.`,
		displayPath: `${pathStart}|Update Certification Button`,
		displayType: 'Button',
		...sharedProperties,
	},
	CPOtherAchievementFormNotificationSuccess: {
		defaultValue: 'Changes saved',
		description: `Succeess notification for creating/editing other achievements.`,
		displayPath: `${pathStart}|SUccess`,
		displayType: 'Notification',
		...sharedProperties,
	},
	CPOtherAchievementFormErrorDateComparison: {
		defaultValue: 'Expiration date must be after achievement date',
		description: `Error shown when expiration date is before achievement date.`,
		displayPath: `${pathStart}|Invalid Date Combination`,
		displayType: 'Error',
		...sharedProperties,
	},
};

export type CPOtherAchievementFormLocType = LocMap<CPOtherAchievementFormLocNames>;
